/**
 * Dev Cart Drawer — Custom cart drawer interactions.
 *
 * Handles:
 * - Free shipping progress bar
 * - Subscription toggle (add/remove selling plan)
 * - Frequency dropdown (change selling plan)
 * - Product recommendations ("You May Also Like") — Subscribe & Save price for subscribers
 * - Add-to-cart from recommendations
 * - Quantity controls (decrease/increase/remove)
 */

const DEV_CART_FREE_SHIPPING_THRESHOLD = 10000; // in cents — $100.00

class DevCartDrawerController {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  constructor() {
    this.#init();
    this.#observeMutations();
  }

  #init() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // Free shipping bar
    this.#updateShippingBar();

    // Subscription toggles
    document.querySelectorAll('.dev-cart__toggle input').forEach((toggle) => {
      toggle.addEventListener('change', (e) => this.#handleSubscriptionToggle(e), { signal });
    });

    // Frequency dropdowns
    document.querySelectorAll('.dev-cart__frequency-select').forEach((select) => {
      select.addEventListener('change', (e) => this.#handleFrequencyChange(e), { signal });
    });

    // Quantity controls
    document.querySelectorAll('.dev-cart__qty-btn[data-action]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.#handleQuantityClick(e), { signal });
    });

    // Recommendation add-to-cart
    document.querySelectorAll('.dev-cart__rec-add-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => this.#handleRecAddToCart(e), { signal });
    });

    // Load recommendations
    this.#loadRecommendations();
  }

  #observeMutations() {
    let debounceTimer = null;
    this.#mutationObserver = new MutationObserver(() => {
      // Debounce re-init to avoid loops from recommendation rendering
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this.#init(), 100);
    });

    const target = document.querySelector('.dev-cart__inner');
    if (target) {
      this.#mutationObserver.observe(target, { childList: true, subtree: false });
    }
  }

  // ─── Free Shipping Bar ──────────────────────────────────────

  #updateShippingBar() {
    const bar = document.querySelector('.dev-cart__shipping-fill');
    const text = document.querySelector('.dev-cart__shipping-amount');
    const banner = document.querySelector('.dev-cart__shipping-banner');
    if (!bar || !text || !banner) return;

    const totalCents = parseInt(banner.dataset.cartTotal || '0', 10);
    const threshold = parseInt(banner.dataset.threshold || DEV_CART_FREE_SHIPPING_THRESHOLD, 10);
    // Subscription orders ship free whatever the total
    const freeShipping = banner.dataset.freeShipping === 'true';
    const remaining = freeShipping ? 0 : Math.max(0, threshold - totalCents);
    const pct = freeShipping ? 100 : Math.min(100, (totalCents / threshold) * 100);

    bar.style.width = `${pct}%`;

    if (remaining <= 0) {
      text.parentElement.innerHTML = `<span class="dev-cart__shipping-text">You've unlocked <span class="dev-cart__shipping-amount">free shipping!</span></span>`;
    } else {
      text.textContent = this.#formatMoney(remaining);
    }
  }

  // ─── Subscription Toggle ────────────────────────────────────

  async #handleSubscriptionToggle(e) {
    const toggle = e.target;
    const itemEl = toggle.closest('.dev-cart__item');
    if (!itemEl) return;

    const variantId = itemEl.dataset.variantId;
    const quantity = parseInt(itemEl.dataset.quantity || '1', 10);
    const lineKey = itemEl.dataset.lineKey;
    const isSubscribing = toggle.checked;

    // Get selling plan ID
    let sellingPlanId = null;
    if (isSubscribing) {
      sellingPlanId = itemEl.dataset.defaultSellingPlan || null;
      if (!sellingPlanId) {
        toggle.checked = false;
        return;
      }
    }

    itemEl.classList.add('is-updating');

    try {
      // Remove current line item
      await this.#cartChange({ id: lineKey, quantity: 0 });

      // Re-add with/without selling plan
      const addBody = {
        id: parseInt(variantId, 10),
        quantity: quantity,
      };
      if (sellingPlanId) {
        addBody.selling_plan = parseInt(sellingPlanId, 10);
      }

      await this.#cartAdd(addBody);

      // Refresh the drawer
      this.#refreshDrawer();
    } catch (err) {
      console.error('Subscription toggle failed:', err);
      toggle.checked = !toggle.checked;
      itemEl.classList.remove('is-updating');
    }
  }

  // ─── Frequency Change ───────────────────────────────────────

  async #handleFrequencyChange(e) {
    const select = e.target;
    const itemEl = select.closest('.dev-cart__item');
    if (!itemEl) return;

    const variantId = itemEl.dataset.variantId;
    const quantity = parseInt(itemEl.dataset.quantity || '1', 10);
    const lineKey = itemEl.dataset.lineKey;
    const newPlanId = select.value;

    if (!newPlanId) return;

    itemEl.classList.add('is-updating');

    try {
      // Remove current line item
      await this.#cartChange({ id: lineKey, quantity: 0 });

      // Re-add with new selling plan
      await this.#cartAdd({
        id: parseInt(variantId, 10),
        quantity: quantity,
        selling_plan: parseInt(newPlanId, 10),
      });

      // Refresh the drawer
      this.#refreshDrawer();
    } catch (err) {
      console.error('Frequency change failed:', err);
      itemEl.classList.remove('is-updating');
    }
  }

  // ─── Quantity Controls ──────────────────────────────────────

  async #handleQuantityClick(e) {
    const btn = e.currentTarget;
    const itemEl = btn.closest('.dev-cart__item');
    if (!itemEl) return;

    const action = btn.dataset.action;
    const lineKey = itemEl.dataset.lineKey;
    const currentQty = parseInt(itemEl.dataset.quantity || '1', 10);

    let newQty;
    if (action === 'decrease') {
      newQty = currentQty - 1;
    } else if (action === 'increase') {
      newQty = currentQty + 1;
    } else {
      return;
    }

    if (newQty < 0) return;

    itemEl.classList.add('is-updating');

    if (newQty === 0) {
      itemEl.classList.add('is-removing');
    }

    try {
      await this.#cartChange({ id: lineKey, quantity: newQty });
      this.#refreshDrawer();
    } catch (err) {
      console.error('Quantity change failed:', err);
      itemEl.classList.remove('is-updating', 'is-removing');
    }
  }

  // ─── Product Recommendations ────────────────────────────────

  async #loadRecommendations() {
    const container = document.querySelector('.dev-cart__recommendations-list');
    if (!container) return;

    // Only fetch via JS for Shopify recommendations source
    const recSection = container.closest('.dev-cart__recommendations');
    if (recSection?.dataset?.recSource && recSection.dataset.recSource !== 'shopify') return;

    // Get product IDs from cart items
    const cartItems = document.querySelectorAll('.dev-cart__item[data-product-id]');
    if (cartItems.length === 0) return;

    // Collect IDs of products already in cart
    const cartProductIds = new Set();
    cartItems.forEach((item) => cartProductIds.add(item.dataset.productId));

    // Use the first product for recommendations
    const productId = cartItems[0].dataset.productId;

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=6&intent=related`
      );
      if (!response.ok) throw new Error('Recommendations fetch failed');

      const data = await response.json();
      const products = (data.products || [])
        .filter((p) => !cartProductIds.has(String(p.id)))
        .slice(0, 2);

      if (products.length === 0) {
        container.closest('.dev-cart__recommendations')?.remove();
        return;
      }

      // A subscriber sees (and adds) the Subscribe & Save price, not the one-time price
      const subscribe = recSection?.dataset?.subscribe === 'true';
      const planName = recSection?.dataset?.planName || '';
      const offers = await Promise.all(
        products.map((p) => (subscribe ? this.#getSubscriptionOffer(p, planName) : null))
      );

      const percents = {
        sale: Number(recSection?.dataset?.salePercent) || 0,
        offer: Number(recSection?.dataset?.offerPercent) || 0,
      };
      container.innerHTML = products.map((p, i) => this.#renderRecCard(p, offers[i], percents)).join('');

      // Bind add-to-cart buttons
      container.querySelectorAll('.dev-cart__rec-add-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => this.#handleRecAddToCart(e));
      });
    } catch {
      // Silently fail — recommendations are optional
      container.closest('.dev-cart__recommendations')?.remove();
    }
  }

  /**
   * The product's Subscribe & Save price for its first available variant.
   * Null (one-time) when the configured plan name doesn't match any of its plans.
   * @returns {Promise<{ variantId: number, sellingPlanId: number, price: number, compareAt: number } | null>}
   */
  async #getSubscriptionOffer(product, planName) {
    try {
      let full = product;
      // Recommendation JSON can leave out selling plans — the product endpoint always has them
      if (!full.selling_plan_groups || !full.variants?.some((v) => v.selling_plan_allocations)) {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${product.handle}.js`);
        if (!res.ok) return null;
        full = await res.json();
      }

      const variant = full.variants?.find((v) => v.available) || full.variants?.[0];
      const allocations = variant?.selling_plan_allocations || [];
      if (allocations.length === 0) return null;

      const normalize = (name) => String(name || '').replace(/&amp;/g, '&').trim();
      const wanted = normalize(planName);
      const plans = (full.selling_plan_groups || []).flatMap((g) => g.selling_plans || []);
      const namedPlan = plans.find((sp) => normalize(sp.name) === wanted);
      // No match for the configured plan name: treat it as one-time rather than guess a plan
      const allocation = namedPlan && allocations.find((a) => a.selling_plan_id === namedPlan.id);
      if (!allocation) return null;

      return {
        variantId: variant.id,
        sellingPlanId: allocation.selling_plan_id,
        price: allocation.per_delivery_price ?? allocation.price,
        compareAt: variant.price,
      };
    } catch {
      return null;
    }
  }

  /**
   * Shopify rounds a percentage discount down to the cent per unit
   * ($49.99 at 25% = $37.50), so the card matches what the cart charges.
   * @param {number} cents
   * @param {number} percent
   */
  #discounted(cents, percent) {
    return cents - Math.floor((cents * percent) / 100);
  }

  /**
   * @param {{ sale: number, offer: number }} percents - sitewide sale and discount-link %,
   *   one-time cards only (a subscriber's card shows the Subscribe & Save price instead)
   */
  #renderRecCard(product, offer = null, percents = { sale: 0, offer: 0 }) {
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    const basePrice = variant?.price || product.price;
    const salePct = offer ? 0 : percents.sale;
    const price = offer ? offer.price : this.#discounted(basePrice, salePct);
    const compareAt = offer ? offer.compareAt : salePct > 0 ? basePrice : variant?.compare_at_price || 0;
    // Discount-link price, revealed by dev-offer-price.js for visitors who came via the link
    const linkPrice = !offer && percents.offer > salePct ? this.#discounted(basePrice, percents.offer) : null;
    const variantId = offer ? offer.variantId : variant?.id;
    const image = product.featured_image || product.images?.[0];
    const imgUrl = image ? this.#getImageUrl(image, '200x200') : '';
    return `
      <div class="dev-cart__rec-card">
        <div class="dev-cart__rec-product">
          <div class="dev-cart__rec-image">
            ${imgUrl ? `<img src="${imgUrl}" alt="${this.#escapeHtml(product.title)}" loading="lazy">` : ''}
          </div>
          <div class="dev-cart__rec-info">
            <div class="dev-cart__rec-details">
              <p class="dev-cart__rec-name">${this.#escapeHtml(product.title)}</p>
              ${(product.description || product.body_html) ? `<p class="dev-cart__rec-desc">${this.#stripHtml(product.description || product.body_html).slice(0, 150)}</p>` : ''}
            </div>
            <div class="dev-cart__rec-pricing">
              <span class="dev-cart__rec-price"${linkPrice !== null ? ' data-offer="regular"' : ''}>
                ${compareAt > price ? `<span class="dev-cart__price-compare">${this.#formatMoney(compareAt)}</span>` : ''}
                <span>${this.#formatMoney(price)}</span>
              </span>
              ${linkPrice !== null ? `
              <span class="dev-cart__rec-price" data-offer="alt" hidden>
                <span class="dev-cart__price-compare">${this.#formatMoney(basePrice)}</span>
                <span>${this.#formatMoney(linkPrice)}</span>
              </span>` : ''}
              ${offer ? '<span class="dev-cart__rec-plan">with Subscribe &amp; Save</span>' : ''}
            </div>
          </div>
        </div>
        <button
          type="button"
          class="dev-cart__rec-add-btn"
          data-variant-id="${variantId}"
          ${offer ? `data-selling-plan="${offer.sellingPlanId}"` : ''}
        >
          Add to Cart
        </button>
      </div>
    `;
  }

  async #handleRecAddToCart(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('dev-cart__rec-add-btn--loading');
    btn.textContent = 'Adding...';
    btn.disabled = true;

    try {
      const addBody = { id: parseInt(variantId, 10), quantity: 1 };
      if (btn.dataset.sellingPlan) {
        addBody.selling_plan = parseInt(btn.dataset.sellingPlan, 10);
      }
      await this.#cartAdd(addBody);
      this.#refreshDrawer();
    } catch (err) {
      console.error('Add to cart failed:', err);
      btn.classList.remove('dev-cart__rec-add-btn--loading');
      btn.textContent = 'Add to Cart';
      btn.disabled = false;
    }
  }

  // ─── Cart API Helpers ───────────────────────────────────────

  async #cartChange(body) {
    const response = await fetch(`${window.Theme?.routes?.cart_change_url || '/cart/change.js'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
    return response.json();
  }

  async #cartAdd(body) {
    const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
    return response.json();
  }

  #refreshDrawer() {
    // Fetch the section HTML and replace drawer content
    const sectionId = document.querySelector('cart-items-component[data-drawer]')?.dataset?.sectionId;
    if (!sectionId) {
      window.location.reload();
      return;
    }

    fetch(`${window.location.pathname}?sections=${sectionId}`)
      .then((r) => r.json())
      .then((sections) => {
        const html = sections[sectionId];
        if (!html) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Morph the cart drawer inner content
        const newInner = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const currentInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');

        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }

        // Update cart count bubble
        const newCount = doc.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
        if (newCount !== undefined) {
          const count = parseInt(newCount, 10) || 0;
          document.querySelectorAll('.cart-bubble__text').forEach((el) => {
            el.textContent = count > 99 ? '99+' : String(count);
          });
          document.querySelectorAll('.cart-bubble').forEach((el) => {
            el.classList.toggle('hidden', count === 0);
          });

          // Handle empty cart
          const dialog = document.querySelector('.dev-cart-dialog');
          if (dialog && count === 0) {
            dialog.classList.add('cart-drawer--empty');
          } else if (dialog) {
            dialog.classList.remove('cart-drawer--empty');
          }
        }

        // Re-init
        this.#init();
      })
      .catch((err) => {
        console.error('Drawer refresh failed:', err);
        window.location.reload();
      });
  }

  // ─── Utilities ──────────────────────────────────────────────

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const dialog = document.querySelector('.dev-cart-dialog');
    const format = dialog?.dataset?.moneyFormat || '${{amount}}';
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
  }

  #getImageUrl(url, size) {
    if (typeof url === 'object') url = url.src || url.url || '';
    if (!url) return '';
    return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
  }

  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  #stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DevCartDrawerController());
} else {
  new DevCartDrawerController();
}
